import {
  Card, Title, Text,
  Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell,
} from '@tremor/react';
import type { PartInvRow, Role } from '../api/types';
import { t } from '../lib/i18n';

const mxn = (n: number | null) =>
  n === null
    ? '—'
    : n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 });

interface Props {
  inv: PartInvRow[];
  role: Role;
  /** JCD-56 — when set, each row offers an "Editar" action for that store.
   *  Omitted when the user cannot edit, so the column disappears entirely
   *  rather than rendering a disabled control. */
  onEdit?: (sid: number) => void;
}

export default function PartInvByStoreTable({ inv, role, onEdit }: Props) {
  // Staff sessions get cost1/cost2 stripped server-side (already null) — also
  // hide the columns entirely so they don't see empty placeholders where the
  // costs live.
  const showCost = role !== 'staff';
  return (
    <Card>
      <Title>{t.part.inv.title}</Title>
      <Table className="mt-2">
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t.part.inv.cols.store}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.part.inv.cols.onHand}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.part.inv.cols.min}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.part.inv.cols.max}</TableHeaderCell>
            <TableHeaderCell>{t.part.inv.cols.bin}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.part.inv.cols.plist}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.part.inv.cols.price1}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.part.inv.cols.price2}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.part.inv.cols.price3}</TableHeaderCell>
            {showCost && (
              <TableHeaderCell className="text-right">{t.part.inv.cols.cost1}</TableHeaderCell>
            )}
            {showCost && (
              <TableHeaderCell className="text-right">{t.part.inv.cols.cost2}</TableHeaderCell>
            )}
            <TableHeaderCell>{t.part.inv.cols.phydate}</TableHeaderCell>
            {onEdit && <TableHeaderCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {inv.length === 0 ? (
            <TableRow>
              <TableCell colSpan={(showCost ? 12 : 10) + (onEdit ? 1 : 0)}>
                <Text>—</Text>
              </TableCell>
            </TableRow>
          ) : (
            inv.map((r) => (
              <TableRow key={r.sid}>
                <TableCell>{r.store_name || r.sid}</TableCell>
                <TableCell className="text-right">{r.on_hand}</TableCell>
                <TableCell className="text-right">{r.min1}</TableCell>
                <TableCell className="text-right">{r.max1}</TableCell>
                <TableCell>{r.bin || '—'}</TableCell>
                <TableCell className="text-right">{mxn(r.plist_mxn)}</TableCell>
                <TableCell className="text-right">{mxn(r.price1_mxn)}</TableCell>
                <TableCell className="text-right">{mxn(r.price2_mxn)}</TableCell>
                <TableCell className="text-right">{mxn(r.price3_mxn)}</TableCell>
                {showCost && (
                  <TableCell className="text-right">{mxn(r.cost1_mxn)}</TableCell>
                )}
                {showCost && (
                  <TableCell className="text-right">{mxn(r.cost2_mxn)}</TableCell>
                )}
                <TableCell>{r.phydate ?? '—'}</TableCell>
                {onEdit && (
                  <TableCell>
                    <button
                      type="button"
                      className="text-tremor-brand hover:underline"
                      onClick={() => onEdit(r.sid)}
                    >
                      {t.part.edit.open}
                    </button>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );
}
